import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Plus,
  Settings,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  isDateLike,
  type CustomField,
  type CustomFieldInput,
  type CustomFieldType,
} from "@/lib/service-detail";
import { ConfirmDialog } from "./ConfirmDialog";
import { CustomFieldPanel, type ScopeBody } from "./CustomFieldPanel";
import { EmptyState } from "./EmptyState";
import { useToast } from "./Toast";

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Textarea" },
  { value: "number", label: "Number" },
  { value: "select", label: "Select" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date and Time" },
  { value: "file", label: "File" },
  { value: "checkbox", label: "Checkbox" },
];

export type CustomFieldsManagerCopy = {
  /** Header description under the "Custom fields" title. */
  description: string;
  /** Empty-state body copy when no fields exist yet. */
  emptyDescription: string;
  /** Placeholder for the new-row label input. */
  newPlaceholder: string;
  /** Confirm-delete description copy. */
  deleteDescription: string;
};

/**
 * Tenant-wide / per-service custom-field schema builder. Same UI lifted
 * from `pages/service/CustomFields.tsx` so Settings → Customers,
 * Settings → Inventory, and Service Detail → Custom fields all render the
 * exact same chrome. The caller owns:
 *   - the field array (already loaded)
 *   - mutations for create / update / delete / reorder
 *   - a cache-invalidation callback fired after every successful change
 */
export function CustomFieldsManager({
  fields,
  copy,
  onCreate,
  onUpdate,
  onDelete,
  onReorder,
  afterChange,
  scopeBody,
}: {
  fields: CustomField[];
  copy: CustomFieldsManagerCopy;
  onCreate: (input: CustomFieldInput) => Promise<CustomField>;
  onUpdate: (
    fieldId: string,
    body: Partial<CustomFieldInput>,
  ) => Promise<CustomField>;
  onDelete: (fieldId: string) => Promise<void>;
  onReorder: (ids: string[]) => Promise<void>;
  afterChange: () => void;
  /** Optional Scope tab passed through to the per-field edit drawer.
   *  See `<CustomFieldPanel />` for the contract. */
  scopeBody?: ScopeBody;
}) {
  const toast = useToast();

  const [showNew, setShowNew] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CustomField | null>(null);
  const [openFieldId, setOpenFieldId] = useState<string | null>(null);
  const openField = fields.find((f) => f.id === openFieldId) ?? null;
  // The date field (if any) that already owns the "booking date" flag, used to
  // block setting a second one from the edit drawer.
  const primaryDateField = fields.find(
    (f) => isDateLike(f.type) && f.settings?.primaryDate,
  );
  const primaryDateTakenBy =
    primaryDateField && primaryDateField.id !== openFieldId
      ? primaryDateField.label
      : null;

  const onMutationError = (e: unknown) =>
    toast.error("Couldn't save", (e as Error).message);

  const create = useMutation({
    mutationFn: (input: CustomFieldInput) => onCreate(input),
    onSuccess: (saved) => {
      afterChange();
      setShowNew(false);
      toast.success("Field added");
      // Auto-open the panel so the operator can configure options + advanced
      // settings immediately after creating the row.
      setOpenFieldId(saved.id);
    },
    onError: onMutationError,
  });

  const remove = useMutation({
    mutationFn: (fieldId: string) => onDelete(fieldId),
    onSuccess: () => {
      afterChange();
      setPendingDelete(null);
      toast.success("Field deleted");
    },
    onError: (e) => {
      onMutationError(e);
      setPendingDelete(null);
    },
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => onReorder(ids),
    onSuccess: afterChange,
    onError: onMutationError,
  });

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    reorder.mutate(next.map((f) => f.id));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold tracking-tight">Custom fields</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {copy.description}
          </p>
        </div>
        <Button size="sm" onClick={() => setShowNew(true)} disabled={showNew}>
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Field
        </Button>
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        {fields.length === 0 && !showNew && (
          <EmptyState
            bare
            icon={SlidersHorizontal}
            title="No custom fields yet"
            description={copy.emptyDescription}
            action={
              <Button size="sm" onClick={() => setShowNew(true)}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add your first field
              </Button>
            }
          />
        )}

        {(fields.length > 0 || showNew) && (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b border-border text-left">
                <th className="w-10" />
                <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Field name
                </th>
                <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground w-32">
                  Type
                </th>
                <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground w-24 text-center">
                  Required
                </th>
                <th className="w-20" />
              </tr>
            </thead>
            <tbody>
              {fields.map((f, idx) => (
                <FieldRow
                  key={f.id}
                  field={f}
                  isFirst={idx === 0}
                  isLast={idx === fields.length - 1}
                  onMoveUp={() => move(idx, -1)}
                  onMoveDown={() => move(idx, 1)}
                  onOpen={() => setOpenFieldId(f.id)}
                  onDelete={() => setPendingDelete(f)}
                />
              ))}
              {showNew && (
                <NewFieldRow
                  pending={create.isPending}
                  placeholder={copy.newPlaceholder}
                  onCancel={() => setShowNew(false)}
                  onCreate={(input) => create.mutate(input)}
                />
              )}
            </tbody>
          </table>
        )}
      </div>

      <CustomFieldPanel
        field={openField}
        onClose={() => setOpenFieldId(null)}
        onSave={(body) => onUpdate(openField!.id, body)}
        onSaveSuccess={afterChange}
        scopeBody={scopeBody}
        primaryDateTakenBy={primaryDateTakenBy}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
        title={`Delete "${pendingDelete?.label}"?`}
        description={copy.deleteDescription}
        confirmLabel="Delete field"
        pending={remove.isPending}
      />
    </div>
  );
}

function FieldRow({
  field,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onOpen,
  onDelete,
}: {
  field: CustomField;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <tr
      className="border-b border-border last:border-0 group cursor-pointer hover:bg-muted/30"
      onClick={onOpen}
    >
      <td className="px-2 align-middle" onClick={stop}>
        <div className="flex flex-col items-center gap-0.5 text-muted-foreground">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="h-4 w-4 inline-flex items-center justify-center rounded hover:bg-muted disabled:opacity-30"
            aria-label="Move up"
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <GripVertical className="h-3.5 w-3.5 opacity-40" />
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="h-4 w-4 inline-flex items-center justify-center rounded hover:bg-muted disabled:opacity-30"
            aria-label="Move down"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="font-medium text-sm truncate">{field.label}</p>
        {field.settings?.helpText && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {field.settings.helpText}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1">
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium capitalize text-foreground/70">
            {field.type}
          </span>
          {isDateLike(field.type) && field.settings?.primaryDate && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
              Main date
            </span>
          )}
          {isDateLike(field.type) && field.settings?.showOnCalendar && (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground/70">
              Calendar
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        {field.required ? (
          <span className="text-xs text-status-pending font-medium">Required</span>
        ) : (
          <span className="text-xs text-muted-foreground">Optional</span>
        )}
      </td>
      <td className="px-2 text-right" onClick={stop}>
        <div className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-0.5 transition">
          <button
            type="button"
            onClick={onOpen}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60"
            aria-label={`Open settings for ${field.label}`}
          >
            <Settings className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            aria-label={`Delete ${field.label}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function NewFieldRow({
  pending,
  placeholder,
  onCancel,
  onCreate,
}: {
  pending: boolean;
  placeholder: string;
  onCancel: () => void;
  onCreate: (input: CustomFieldInput) => void;
}) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState<CustomFieldType>("text");
  const [required, setRequired] = useState(false);

  const submit = () => {
    if (!label.trim()) return;
    onCreate({ label: label.trim(), type, required });
  };

  return (
    <tr className="bg-primary-light/30 border-b border-border">
      <td className="px-2 text-center text-muted-foreground">
        <Plus className="h-3.5 w-3.5 inline-block opacity-60" />
      </td>
      <td className="px-4 py-2.5">
        <Label htmlFor="new-field-label" className="sr-only">
          New field label
        </Label>
        <Input
          id="new-field-label"
          autoFocus
          placeholder={placeholder}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onCancel();
          }}
          className="h-9"
        />
      </td>
      <td className="px-4 py-2.5">
        <Select value={type} onValueChange={(v) => setType(v as CustomFieldType)}>
          <SelectTrigger className="h-9 capitalize">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FIELD_TYPES.map((ft) => (
              <SelectItem key={ft.value} value={ft.value}>
                {ft.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-4 py-2.5 text-center">
        <Switch
          checked={required}
          onCheckedChange={setRequired}
          aria-label="Required (new field)"
        />
      </td>
      <td className="px-2 text-right">
        <div className="flex items-center gap-1 justify-end">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!label.trim() || pending}>
            {pending ? "Adding…" : "Add"}
          </Button>
        </div>
      </td>
    </tr>
  );
}
